import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { BoardBackdrop } from "./BoardBackdrop.js";
import { Logo } from "./Logo.js";

/**
 * The frame the account screens share — sign in, register, verify, forgot,
 * reset.
 *
 * Presentational and props-only, like everything in `components/`: it takes a
 * title and children and knows nothing about sessions, parties or the server.
 *
 * # It wears the same face as the front door
 *
 * The mark and the lit backdrop are here, not just on the landing. They were
 * landing-only at first, and the effect was that walking from a page with a
 * glowing record and a coloured background to "Sign in" on flat black made the
 * app look like two products stitched together — at exactly the moment somebody
 * is deciding whether to trust it with an email address.
 *
 * The mark is a link home, because a wordmark that is not clickable is the
 * first thing every user tries when they want out of a form. It is smaller here
 * than on the landing: this screen's job is the form under it, and a hero-sized
 * record above a password field is decoration competing with the task.
 *
 * `useTranslation` is the single exception to "components take props": the
 * alternative is every page threading the same strings down, which is how
 * copy drifts. It reads translations; it still receives all its *data* as
 * props.
 */
export function Screen({
  title,
  lead,
  children,
  footer,
}: {
  title: string;
  /** One line under the title, when the title alone is not enough. */
  lead?: string | undefined;
  children: React.ReactNode;
  /** Links out — sign in, create an account, go back. */
  footer?: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className="relative min-h-full">
      <BoardBackdrop />

      <main className="relative flex min-h-full flex-col items-center justify-center gap-8 p-6">
        <div className="w-full max-w-sm">
          <header className="flex flex-col items-center text-center">
            <Link
              to="/"
              className="group flex flex-col items-center"
              aria-label={t("app.name")}
            >
              <Logo size={64} />
              <span className="wordmark mt-2 text-lg font-bold tracking-tight">
                {t("app.name")}
              </span>
            </Link>
            {/* The screen's own title stays the `h1`: on these pages "Sign in"
                or "Check your inbox" is what the page *is*, and the mark above
                it says which app it belongs to. */}
            <h1 className="mt-5 text-3xl font-semibold">{title}</h1>
            {lead && (
              <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-white/55">
                {lead}
              </p>
            )}
          </header>

          <div className="mt-6 flex flex-col gap-4">{children}</div>

          {footer && (
            <div className="mt-6 flex flex-col items-center gap-2 text-sm text-white/50">
              {footer}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/** A form's own error line. `role="alert"` so it is announced, not just shown. */
export function ErrorLine({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="text-sm text-wrong-500">
      {children}
    </p>
  );
}

/**
 * A place to go, shaped like something to press.
 *
 * # Why it is still a `Link`
 *
 * Phase 20a, on a phone: three 12px sentences stacked under a form are a bad
 * target and read as fine print rather than as the three ways on from this
 * screen. They needed to be buttons.
 *
 * They are **not** `<button>` elements, though, and the distinction is not
 * pedantry. These navigate, and navigation belongs in an anchor: long-press,
 * middle-click, open-in-new-tab, "copy link address" and a screen reader saying
 * *link* all come from the element, not from the styling. A `<button>` with an
 * `onClick` that calls `navigate()` throws all of that away in exchange for
 * looking the same.
 *
 * So: anchor semantics, button affordance, and a 48px target — deliberately
 * shorter than [`Action`]'s 56px, because these are the ways *past* the form
 * and the form is the thing to press.
 */
export function LinkAction({
  to,
  icon: Icon,
  children,
  accent,
}: {
  to: string;
  /** A lucide icon component. Decorative — the label carries the meaning. */
  icon: LucideIcon;
  children: React.ReactNode;
  /** The one of a set worth leading with, if there is one. */
  accent?: boolean;
}) {
  return (
    <Link
      to={to}
      className={
        "flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border px-3 text-center text-sm font-medium leading-tight transition-colors " +
        (accent
          ? "border-flash-500/40 bg-flash-500/10 text-flash-300 hover:bg-flash-500/15"
          : "border-white/10 bg-stage-900/60 text-white/70 hover:bg-stage-900 hover:text-white/90")
      }
    >
      <Icon size={16} strokeWidth={2} className="shrink-0" aria-hidden="true" />
      {children}
    </Link>
  );
}

/** The one button shape these screens use. */
export function Action({
  children,
  disabled,
  type = "submit",
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  type?: "submit" | "button";
  onClick?: () => void;
}) {
  return (
    <button
      type={type === "submit" ? "submit" : "button"}
      disabled={disabled}
      onClick={onClick}
      // `text-stage-950` on `bg-flash-500` is the brand's own dark pairing
      // (--accent / --accent-fg) and clears 4.5:1; the glow is dropped while
      // disabled so a button that cannot be pressed does not advertise itself as
      // the thing to press.
      className="neon min-h-14 rounded-lg bg-flash-500 px-4 text-lg font-semibold text-stage-950 transition-shadow hover:shadow-[0_14px_34px_-12px_var(--glow)] disabled:opacity-40 disabled:shadow-none"
    >
      {children}
    </button>
  );
}
